import { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import { updateReaction } from "../../api/chatMessage";
import { Drawer, Survey } from "../common";
import useAnalytics from "../../hooks/useAnalytics";
import {
  useChatFAQStream,
  useChatStream,
  useSmoothScroll,
} from "../../hooks/useChat";
import useTimer from "../../hooks/useTimer";
import { useChatBotConfigContext } from "../../providers/chatBotConfigContext";
import ChatBotPosition from "../../types/chatBotConfig/enums";
import isQuestion from "../../utils/isQuestion";
import en from "../../utils/locales/en";
import { Body } from "./body";
import { Footer } from "./footer";

import { GlobalStateContext } from "../../../../context/GlobalStateContext";

import { PreselectedQuestions } from "./preselectedQuestions";
import styles from "./styles.module.css";

export const ChatBot = () => {
  const getTermsValueFromLS = () => {
    const valueFromLS = localStorage.getItem("aiiro-agree-with-terms");

    if (!valueFromLS) localStorage.setItem("aiiro-agree-with-terms", "false");

    return valueFromLS === "true";
  };

  const {
    chatBotConfig,
    isChatBotOpen,
    handleCloseChatBot,
    handleCloseFaqs,
    isFaqsOpen,
    chatBotFAQsQuestions,
  } = useChatBotConfigContext();
  const { captureEvent } = useAnalytics();

  const chatContainerRef = useRef(null);
  const isEndedChat = useRef(false);

  const [isChatLoading, setIsChatLoading] = useState(false);
  const { messages, setMessages } = useContext(GlobalStateContext);
  const [newMessageText, setNewMessageText] = useState("");
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);
  const [isAgreeWithTerms, setIsAgreeWithTerms] = useState(getTermsValueFromLS);
  const [isShowAgreeWithTerm] = useState(() => !getTermsValueFromLS());

  const scrollToBottom = useSmoothScroll(chatContainerRef);

  const handleAppendMessage = (newMessageData) => {
    if (isEndedChat.current) return;

    setMessages((messages) => {
      if (!messages.find((message) => newMessageData.id === message.id)) {
        scrollToBottom();

        return [...messages, newMessageData];
      }

      return messages.map((message) => {
        if (message.id !== newMessageData.id) return message;

        scrollToBottom(false);

        return {
          id: newMessageData.id,
          text: message.text + newMessageData.text,
          time: newMessageData.time,
          isBotMessage: newMessageData.isBotMessage,
        };
      });
    });
  };

  const handleSetMessage = (message) => {
    if (isEndedChat.current) return;

    setMessages((prev) => [...prev, message]);
  };

  const handleReplaceId = (oldId, newMessageData) => {
    if (isEndedChat.current) return;

    setMessages((prev) =>
      prev.map((message) => (oldId === message.id ? newMessageData : message))
    );
  };

  const { handleSendMessage } = useChatStream({
    setNewMessageText,
    setMessage: handleSetMessage,
    appendMessage: handleAppendMessage,
    setIsChatLoading: setIsChatLoading,
    replaceId: handleReplaceId,
    messages: messages,
  });

  // send faq message
  const { handleSendFAQMessage } = useChatFAQStream({
    setNewMessageText,
    setMessage: handleSetMessage,
    appendMessage: handleAppendMessage,
    setIsChatLoading: setIsChatLoading,
  });

  // Close session, if no activity for a special mins.
  const MAX_DELAY_MESSAGE = 5 * 60 * 1000;

  const {
    startTimer: startMessageTimer,
    resetTimer: resetMessageTimer,
  } = useTimer(() => {
    handleCollapseAll();
  }, MAX_DELAY_MESSAGE);

  useEffect(() => {
    if (isEndedChat.current && isChatBotOpen) isEndedChat.current = false;
    if (isChatBotOpen) {
      startMessageTimer();
      scrollToBottom(false);
    } else {
      handleCollapseAll();
    }
  }, [isChatBotOpen]);

  const handleMessageReactions = (id, liked) => {
    updateReaction(id, liked);

    setMessages((messages) => {
      return messages.map((message) => {
        if (message.id !== id) return message;

        return {
          ...message,
          isReactable: true,
          isLiked: liked,
        };
      });
    });
  };

  const handleSendFAQMessageWithScroll = async (faq) => {
    if (isChatLoading || !isAgreeWithTerms) return;

    setNewMessageText("");

    // send event for faq
    captureEvent("FaqQuestion", { query: faq.question });

    resetMessageTimer();

    await handleSendFAQMessage(faq, (smooth) => {
      setTimeout(() => {
        scrollToBottom?.(smooth || false);
      }, 300);
    });
  };

  const handleSendMessageWithScroll = async (faq) => {
    if (isChatLoading || !isAgreeWithTerms) return;
    if (newMessageText.trim() === "") return;

    if (faq) {
      setNewMessageText("");

      // send event for faq
      captureEvent("FaqQuestion", { query: faq });
    } else {
      // send event
      captureEvent("Message", { query: newMessageText });

      if (isQuestion(newMessageText)) {
        captureEvent("Question", { query: newMessageText });
      }
    }

    resetMessageTimer();
    await handleSendMessage(
      faq || newMessageText,
      chatBotFAQsQuestions || [],
      (smooth) => {
        setTimeout(() => {
          scrollToBottom?.(smooth || false);
        }, 300);
      }
    );
  };

  const handleCollapseAll = useCallback(() => {
    handleCloseFaqs();
    handleCloseChatBot();
  }, [handleCloseChatBot, handleCloseFaqs]);

  const positionClassName = useMemo(() => {
    switch (chatBotConfig?.position) {
      case ChatBotPosition.EMBEDED:
        return ` ${styles.chatBotPositionBottom}`;
      case ChatBotPosition.RIGHT_PANEL_TOP:
        return ` ${styles.chatBotPositionTop}`;
      case ChatBotPosition.RIGHT_PANEL_MIDDLE:
        return ` ${styles.chatBotPositionMiddle}`;
      case ChatBotPosition.RIGHT_PANEL_BOTTOM:
        return ` ${styles.chatBotPositionBottom}`;
      default:
        return ` ${styles.chatBotPositionBottom}`;
    }
  }, [chatBotConfig?.position]);

  const handleTermsClick = () => {
    setIsAgreeWithTerms((prev) => {
      localStorage.setItem("aiiro-agree-with-terms", String(!prev));
      return !prev;
    });
  };

  return (
    <>
      <Survey
        surveyQuestion={chatBotConfig?.surveyQuestion}
        open={isSurveyOpen}
        onModalChange={setIsSurveyOpen}
      />

      <div
        className={`${styles.chatBotWidget}${positionClassName}${
          isChatBotOpen ? ` ${styles.chatBotWidgetOpen}` : ""
        }`}
      >
        {/* <Header
          isChatLoading={isChatLoading}
          onCloseClick={handleCollapseAll}
          onEndChatClick={handleCloseAll}
          chatTitle={chatBotConfig?.chatTitle}
          isFAQ={chatBotConfig?.isFAQ}
          handleClickOnFaqs={handleToggleFaqs}
          liveText={en.live}
          endChatText={en.endChat}
          FAQsText={en.faqs}
        /> */}
        <Body
          chatContainerRef={chatContainerRef}
          messages={messages}
          messageTitle={chatBotConfig?.messageTitle}
          description={chatBotConfig?.description}
          aIAssistantText={en.aIAssistant}
          meText={en.me}
          onMessageLike={(id) => handleMessageReactions(id, true)}
          onMessageDislike={(id) => handleMessageReactions(id, false)}
          isAgreeWithTerms={isAgreeWithTerms}
          isShowAgreeWithTerm={isShowAgreeWithTerm}
          handleTermsClick={handleTermsClick}
          checkboxLabel={
            <>
              {en.byClickingHere}{" "}
              <a
                href="https://aiiro.webflow.io/terms-of-use"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                rel="noreferrer"
              >
                {en.termsOfUse}
              </a>{" "}
              ,{" "}
              <a
                href="https://aiiro.webflow.io/privacy-policy"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                rel="noreferrer"
              >
                {en.privacyPolicy}
              </a>{" "}
              {en.and}{" "}
              <a
                href="https://aiiro.webflow.io/aiiro-cookies-policy"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                rel="noreferrer"
              >
                {en.cookiesPolicy}
              </a>
              .
            </>
          }
        />
        <Footer
          isDisabled={!isAgreeWithTerms}
          isLoading={isChatLoading}
          onSubmit={handleSendMessageWithScroll}
          newMessageText={newMessageText}
          setNewMessageText={setNewMessageText}
          placeholder={en.typeYourQuestion}
          additionalText={en.thisIsNotFinancial}
          poweredByText={en.poweredBy}
          logoLink="https://aiiro.webflow.io/"
          symbol={chatBotConfig?.stockSymbol || ""}
        />
      </div>

      <PreselectedQuestions
        questions={chatBotFAQsQuestions}
        onSelectQuestion={handleSendFAQMessageWithScroll}
        isFaqsOpen={isFaqsOpen}
        handleCloseFaqs={handleCloseFaqs}
        position={chatBotConfig?.position}
        title={en.preselectedQuestions}
        description={en.preselectedQuestionsDescription}
        buttonText={en.selectQuestion}
      />

      <Drawer isActive={isChatBotOpen} handleClick={handleCollapseAll} />
    </>
  );
};
